import { useQueryClient } from 'react-query';
import { TaskCenterQueries } from '@frontend/api-task-center';
import { queryKeyBase_HasNewTasks } from '../queries/use-has-new-tasks';
import { UpdateTasksSeenStatusIO } from '../types';
import { UseTaskV1MutationEndpointArgs, useTaskV1Mutation } from './use-task-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `UpdateTasksSeenStatus` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useUpdateTasksSeenStatusMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends UpdateTasksSeenStatusIO['input'] = UpdateTasksSeenStatusIO['input']
>({
  options,
  ...args
}: UseTaskV1MutationEndpointArgs<'UpdateTasksSeenStatus', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const queryClient = useQueryClient();

  return useTaskV1Mutation<'UpdateTasksSeenStatus', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'UpdateTasksSeenStatus',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        queryClient.invalidateQueries(TaskCenterQueries.queryEndpointKeys.tasksList);
        queryClient.invalidateQueries(queryKeyBase_HasNewTasks);

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
